import {
    useGetPriorityOrderQuery,
    useListPriorityPlannersQuery, useListPriorityTasksQuery, useUpdatePriorityOrderMutation, useDeletePriorityPlannerMutation
} from "../../apis/priorityApi";
import {Button} from "primereact/button";
import React, {useMemo, useState, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import _ from "lodash";
import {Checkbox} from "primereact/checkbox";
import {useUserProfileQuery} from "../../apis/authenticationApi";
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { Toast } from 'primereact/toast';

const PriorityPlannerTable = () => {
    const navigate = useNavigate();
    const {data: priorities, isLoading: isLoadingPriorities} = useListPriorityPlannersQuery();
    const {data: priorityOrder, isLoading: isLoadingPriorityOrder} = useGetPriorityOrderQuery();
    const {data: tasks, isLoading: isLoadingTasks} = useListPriorityTasksQuery();
    const [updatePriorityOrder] = useUpdatePriorityOrderMutation();
    const [showCompleted, setShowCompleted] = useState(false);
    const {data: userProfile} = useUserProfileQuery();
    const canEdit = userProfile?.company?.role === "CompanyOwner";
    const [deletePriorityPlanner] = useDeletePriorityPlannerMutation();

    const toast = useRef(null);

    const orderedPriorities = useMemo(() => {
        if(isLoadingPriorityOrder || isLoadingPriorities || isLoadingTasks || !priorities || !priorityOrder) {
            return [];
        }

        let count = 0;

        return (priorityOrder?.priorities || []).map((order) => {
            const priority = priorities.find(priority => priority.id === order);

            if(!priority) {
                return {};
            }

            const priorityTasks = tasks.filter(task => task.parent === priority.id);
            const closedTasks = priorityTasks.reduce((total, task) => task.taskStatus === 'Done' ? total + 1 : total, 0);
            const clonedPriority = _.cloneDeep(priority);

            clonedPriority.taskCount = priorityTasks.length;
            clonedPriority.closedTaskCount = closedTasks;

            if(closedTasks > 0 && clonedPriority.taskCount === closedTasks) {
                if(clonedPriority.completed) {
                    clonedPriority.status = "RESOLVED";
                    clonedPriority.order = 6;
                }
                else {
                    clonedPriority.status = "CLOSED";
                    clonedPriority.order = 5;
                }
            } else {
                clonedPriority.order = count < 3 ? count + 1 : 4;
                clonedPriority.status = count < 3 ? (count + 1) + "" : "PARKED";
                count++;
            }

            return clonedPriority;
        })
        .filter(priority => priority.id && (showCompleted || priority.status !== "RESOLVED"))
        .sort((priority1, priority2) => priority1.order - priority2.order);
    }, [showCompleted, priorities, tasks, priorityOrder, isLoadingPriorities, isLoadingPriorityOrder, isLoadingTasks])

    const newPriorityPlanner = () => {
        edit("new");
    }
    const edit = (id) => {
        navigate(`/company-admin/priority-planner/edit/${id}`);
    }

    const deleteEntry = (id, event) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Are you sure you want to proceed? All the associated tasks of the Priority Planner will be deleted as well.',
            icon: 'pi pi-exclamation-triangle',
            accept: () => deletePriorityPlanner(id)
        });
    }

    const header = (
        <>
            <h3>Priority Planner</h3>
            <div className="table-header">
                <div className="button-panel">
                    {canEdit && <Button type="button" label="New" icon="pi pi-plus" tooltip="Add Priority" onClick={newPriorityPlanner} />}
                </div>
                <div className="show-completed-priorities">
                    <Checkbox inputId="show-completed" checked={showCompleted} onChange={e => setShowCompleted(e.checked)} />
                    <label htmlFor="show-completed">Show Resolved Priorities</label>
                </div>
            </div>
        </>
    );

    const onRowReorder = (e) => {
        const newOrder = e.value.map(priority => priority.id);
        updatePriorityOrder({priorities: newOrder});
    }

    const onRowPriorityChange = (e, type) => {
        const itemMoved = orderedPriorities?.find(p => p.id === e?.id);
        
        if(itemMoved) {
            const before = orderedPriorities?.find(p => p.order === (itemMoved.order - 1));
            const after = orderedPriorities?.find(p => p.order === (itemMoved.order + 1));
            if(type === 'up' && itemMoved.order > 1) {
                before.order++;
                itemMoved.order--;
            } else if(type === 'down'  && itemMoved.order < orderedPriorities.length) {
                after.order--;
                itemMoved.order++;
            }
        }
        orderedPriorities.sort((a, b) => (a.order - b.order));

        updatePriorityOrder({priorities: orderedPriorities.map(priority => priority.id)});
    }

    const actionTemplate = (rowData) => {
        return (
            <>
                <Button type="button" icon={"pi " +  (canEdit? "pi-pencil" : "pi-eye")} onClick={() => edit(rowData.id)} />
                {canEdit && <Button type="button" icon="pi pi-times" onClick={(e) => deleteEntry(rowData.id, e)} />}
            </>
        );
    }

    const taskBodyTemplate = (rowData) => {
        return rowData.closedTaskCount + "/" + rowData.taskCount;
    }

    const upBodyTemplate = (rowData) => {
        return  <i className="pi pi-caret-up" style={{color: 'green'}} onClick={() => onRowPriorityChange(rowData, 'up')} />;
    };

    const downBodyTemplate = (rowData) => {
        return  <i className="pi pi-caret-down" style={{color: 'red'}} onClick={() => onRowPriorityChange(rowData, 'down')} />;
    };

    return (
        <div className="priority-planner-table">
            <Toast ref={toast} />
            <ConfirmPopup />
            <div className="mobile-horizontal-scrollable">
                <DataTable value={orderedPriorities} reorderableRows onRowReorder={onRowReorder}
                           header={header}
                           dataKey="id" loading={isLoadingPriorities || isLoadingPriorityOrder}
                           emptyMessage="No Priorities">
                    <Column body={downBodyTemplate} style={{width: '0.1em'}} />
                    <Column body={upBodyTemplate} style={{width: '0.1em'}} />
                    <Column field="status" bodyClassName="p-text-center" headerClassName="centered-header" header="Status" style={{width: '3em'}}></Column>
                    <Column field="name" header="Defining Priorities"></Column>
                    <Column bodyClassName="p-text-center" headerClassName="centered-header" header="Tasks" style={{width: '3em'}} body={taskBodyTemplate}></Column>
                    <Column className="action-column" headerClassName="centered-header" bodyClassName="p-text-center" header="Actions" body={actionTemplate}></Column>
                    <Column rowReorder style={{width: '3em'}} />
                </DataTable>
            </div>
        </div>
    )
}

export default PriorityPlannerTable;
